import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  badgeHidesPublicUnlockStats,
  getRoleAchievementsCatalog,
  getActivityAchievementsCatalog,
} from '../badges/badgeDefinitions';
import { BadgeGraphic } from '../badges/BadgeGraphics';
import {
  fetchBadgeUnlockCounts,
  populationRarityKey,
  formatUnlockPercent,
} from '../../utils/badgeUnlockCountsService';
import './AchievementsPopup.css';

function AchievementRow({ badge, unlocked, unlockCount, totalUsers }) {
  const { t } = useTranslation(['profile']);
  const name = t(badge.labelKey, badge.label);
  const desc = badge.descriptionKey ? t(badge.descriptionKey) : '';
  const hidePopulation = badgeHidesPublicUnlockStats(badge.id);
  const count = typeof unlockCount === 'number' ? unlockCount : 0;
  const total = typeof totalUsers === 'number' ? totalUsers : 0;
  const popKey = populationRarityKey(count, total);
  const popLabel = total > 0 ? t(popKey) : t('achievementsPopup.countsUnavailable');
  const pct = formatUnlockPercent(count, total);
  const populationText =
    total > 0 && pct != null
      ? t('achievementsPopup.populationLine', { popLabel, count, total, pct })
      : total > 0
        ? t('achievementsPopup.populationLineNoPct', { popLabel, count, total })
        : popLabel;

  const rowTitle = desc ? `${name}. ${desc}` : name;

  return (
    <li
      className={`achievements-popup-row achievements-popup-row--${unlocked ? 'unlocked' : 'locked'}`}
      title={rowTitle}
      style={{
        '--badge-color': badge.color,
        '--badge-bg': badge.bgColor,
        '--badge-border': badge.borderColor,
      }}
    >
      <div className="achievements-popup-row-inner">
        <span className="achievements-popup-row-icon" aria-hidden>
          <BadgeGraphic badgeId={badge.id} size={40} />
        </span>
        <div className="achievements-popup-row-main">
          <div className="achievements-popup-row-titleline">
            <span className="achievements-popup-row-name">{name}</span>
            <span
              className={`achievements-popup-status achievements-popup-status--${unlocked ? 'yes' : 'no'}`}
            >
              {unlocked
                ? t('achievementsPopup.statusUnlocked')
                : t('achievementsPopup.statusLocked')}
            </span>
          </div>
          {desc ? <p className="achievements-popup-row-desc">{desc}</p> : null}
          <p
            className={`achievements-popup-row-pop${
              hidePopulation ? ' achievements-popup-row-pop--not-public' : ''
            }`}
          >
            {hidePopulation
              ? t('achievementsPopup.popularityNotPublic')
              : populationText}
          </p>
        </div>
      </div>
    </li>
  );
}

function AchievementsPopup({ userRole, stats, onlyOwned = false }) {
  const { t } = useTranslation(['profile']);
  const [unlockData, setUnlockData] = useState({ totalUsers: 0, badges: {} });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = await fetchBadgeUnlockCounts();
      if (!cancelled) setUnlockData(next);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const roleRowsAll = getRoleAchievementsCatalog(userRole);
  const activityRowsAll = getActivityAchievementsCatalog(stats);
  const roleRows = onlyOwned ? roleRowsAll.filter((row) => row.unlocked) : roleRowsAll;
  const activityRows = onlyOwned ? activityRowsAll.filter((row) => row.unlocked) : activityRowsAll;
  const { totalUsers, badges } = unlockData;

  return (
    <div className="achievements-popup">
      <section className="achievements-popup-section" aria-labelledby="ach-popup-role-heading">
        <h3 id="ach-popup-role-heading" className="achievements-popup-section-title">
          {t('achievementsPopup.roleSection')}
        </h3>
        {roleRows.length === 0 ? (
          onlyOwned ? (
            <p className="achievements-popup-section-empty">
              {t('achievementsPopup.sectionEmptyOnlyOwned')}
            </p>
          ) : null
        ) : (
          <ul className="achievements-popup-list">
            {roleRows.map((row) => (
              <AchievementRow
                key={row.id}
                badge={row}
                unlocked={row.unlocked}
                unlockCount={badges[row.id]}
                totalUsers={totalUsers}
              />
            ))}
          </ul>
        )}
      </section>

      <section className="achievements-popup-section" aria-labelledby="ach-popup-activity-heading">
        <h3 id="ach-popup-activity-heading" className="achievements-popup-section-title">
          {t('achievementsPopup.activitySection')}
        </h3>
        {activityRows.length === 0 ? (
          onlyOwned ? (
            <p className="achievements-popup-section-empty">
              {t('achievementsPopup.sectionEmptyOnlyOwned')}
            </p>
          ) : null
        ) : (
          <ul className="achievements-popup-list">
            {activityRows.map((row) => (
              <AchievementRow
                key={row.id}
                badge={row}
                unlocked={row.unlocked}
                unlockCount={badges[row.id]}
                totalUsers={totalUsers}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default AchievementsPopup;
